import { gql } from '@apollo/client'
import { initializeApollo } from '@jusbrasil-web/shared-apollo-client'
import {
  FEATURE_CLASS,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
} from '@jusbrasil-web/shared-ga4-events'
import { getCookie, setJusbrasilCookie } from '@jusbrasil-web/shared-utils-cookies'
import { isElementSelected } from '@jusbrasil-web/shared-utils-dom'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
// eslint-disable-next-line no-restricted-imports
import memoize from 'lodash/memoize'
// eslint-disable-next-line no-restricted-imports
import once from 'lodash/once'
import { JsonObject } from 'type-fest'
import { v4 as uuid } from 'uuid'

import { BoxUniqueFeatureModalProduct } from '../../types'
import { CopyListenerHelper } from '../listener-helper/listener-helper'
import { loadBoxModal } from '../load-box-modal/load-box-modal'
import {
  CopyContentTriggerMutationMutation,
  ViewerNextPlanModalQuery,
  ViewerNextPlanModalQueryVariables,
  ViewerPidAndCanUseFeatureQuery,
  ViewerPidAndCanUseFeatureQueryVariables,
} from './__generated__/copy-content-trigger-util.graphql'

const client = initializeApollo()

const noop = () => undefined

type CopyContentTriggerUtilEventData = Record<string, unknown> & {
  from_component: string
  metadata?: JsonObject
}

type CopyContentTriggerUtilGA4EventData = {
  componentClass: string
  componentName?: string
  copyFeatureName?: string
}

export interface CopyContentTriggerUtilConfig {
  window?: Window
  boxModalOptions?: JsonObject
  boxModalVersion?: string
  boxOptions?: JsonObject
  element: HTMLElement
  eventData: CopyContentTriggerUtilEventData
  ga4EventData?: CopyContentTriggerUtilGA4EventData
  eventNamespace: string
  product?: BoxUniqueFeatureModalProduct
  sendGA4Event?: CallableFunction
  featureCode: string
  loadBoxModalData?: boolean
  onCopyError?: CallableFunction
  onCopySuccess?: CallableFunction
  showBoxLockModal?: CallableFunction
  onVisibleChange?: (visible: boolean) => void
  onLoadViewerCanUseFeature?: (canUseFeature: boolean) => void
}

type CopyContentTriggerUtilDocument = Record<string, unknown> & {
  artifact?: string
  docId?: number
  viewerCanUseFeature?: NonNullable<
    ViewerPidAndCanUseFeatureQuery['root']['document']
  >['viewerCanUseFeature']
}

interface CopyContentTriggerUtilViewer {
  pid?: NonNullable<ViewerPidAndCanUseFeatureQuery['root']['viewer']>['pid']
  modalData?: NonNullable<ViewerNextPlanModalQuery['root']['viewer']>['modalData']
}

interface CopyContentTriggerUtilDocumentAndViewer {
  document: CopyContentTriggerUtilDocument | null
  viewer: CopyContentTriggerUtilViewer | null
}

export const viewerPidAndCanUseFeatureQuery = gql`
  query ViewerPidAndCanUseFeature($artifact: String!, $docId: NumericID!, $featureCode: String!) {
    root {
      viewer: me {
        pid
      }
      document: documentByNumericID(artifact: $artifact, docId: $docId) {
        viewerCanUseFeature(featureCode: $featureCode)
      }
    }
  }
`

export const viewerNextPlanModal = gql`
  query ViewerNextPlanModal(
    $featureCode: String!
    $appId: String
    $fromComponent: String
    $boxOptions: JSON
  ) {
    root {
      viewer: me {
        modalData: nextPlanModal(
          featureCode: $featureCode
          appId: $appId
          fromComponent: $fromComponent
          boxOptions: $boxOptions
          addPricingPlans: false
        )
      }
    }
  }
`

export const copyContentTriggerMutation = gql`
  mutation CopyContentTriggerMutation($input: UseDocumentFeatureInput!) {
    useDocumentFeature(input: $input) {
      profile {
        id
      }
    }
  }
`

export const GAGoalsFeaturesDataMapping: {
  attempt: Record<string, { event_category: string; event_action: string }>
  success: Record<string, { event_category: string; event_action: string; event_label?: string }>
} = {
  attempt: {
    copy_juris_content: {
      event_category: 'Jurisprudência',
      event_action: 'Copy attempt',
    },
    legal_quote: {
      event_category: 'Jurisprudência',
      event_action: 'Copy Ementa attempt',
    },
    copy_modelos_content: {
      event_category: 'Modelos e Peças',
      event_action: 'Copy attempt',
    },
  },
  success: {
    abnt_quote_doctrine: {
      event_category: 'Legal research success proxies',
      event_action: 'copy',
      event_label: 'DoctrineABNTModal',
    },
    copy_modelos_content: {
      event_category: 'Legal research success proxies',
      event_action: 'copy',
      event_label: 'ModelContent',
    },
    default: {
      event_category: 'Legal research success proxies',
      event_action: 'copy',
    },
  },
}

const BROWSER_HASH_COOKIE_DURATION_IN_YEARS = 10
const cookieKey = 'jb_browser_hash'

export class CopyContentTriggerUtil {
  static readonly boxModalType = 'UNIQUE_FEATURE_MODAL'

  // TODO: refactor ported specs allowing private methods. @moret
  static readonly queryDocAndViewer = memoize(
    (variables, loadBoxModalData): Promise<CopyContentTriggerUtilDocumentAndViewer> =>
      client
        .query<ViewerPidAndCanUseFeatureQuery, ViewerPidAndCanUseFeatureQueryVariables>({
          query: viewerPidAndCanUseFeatureQuery,
          variables,
        })
        .then(
          ({
            data: {
              root: { document, viewer },
            },
          }) => {
            if (!viewer || document?.viewerCanUseFeature || !loadBoxModalData) {
              return { document, viewer }
            }

            return client
              .query<ViewerNextPlanModalQuery, ViewerNextPlanModalQueryVariables>({
                query: viewerNextPlanModal,
                variables,
              })
              .then(
                ({
                  data: {
                    root: { viewer: viewerWithModalData },
                  },
                }) => ({
                  document,
                  viewer: { ...viewer, modalData: viewerWithModalData?.modalData },
                })
              )
          }
        ),
    // Use only featureCode in cache key to avoid server overload
    ({ featureCode }) => featureCode
  )

  private readonly window?: Window
  private readonly boxModalOptions?: CopyContentTriggerUtilConfig['boxModalOptions']
  private readonly boxModalVersion?: CopyContentTriggerUtilConfig['boxModalVersion']
  private readonly boxOptions?: CopyContentTriggerUtilConfig['boxOptions']
  // TODO: refactor ported specs allowing property to become private/protected and readonly. @moret
  document: CopyContentTriggerUtilDocument
  // TODO: refactor ported specs allowing property to become private/protected and readonly. @moret
  readonly element: CopyContentTriggerUtilConfig['element']
  private readonly eventData: CopyContentTriggerUtilConfig['eventData']
  private readonly ga4EventData?: CopyContentTriggerUtilConfig['ga4EventData']
  private readonly eventNamespace: CopyContentTriggerUtilConfig['eventNamespace']
  private readonly product: CopyContentTriggerUtilConfig['product']
  private readonly sendGA4Event?: CopyContentTriggerUtilConfig['sendGA4Event']
  // TODO: refactor ported specs allowing property to become private/protected and readonly. @moret
  readonly featureCode: CopyContentTriggerUtilConfig['featureCode']
  private listenerHelper?: CopyListenerHelper
  private readonly loadBoxModalData: NonNullable<CopyContentTriggerUtilConfig['loadBoxModalData']>
  // TODO: refactor ported specs allowing property to become private/protected and readonly. @moret
  loading: boolean
  // TODO: refactor ported specs allowing property to become private/protected and readonly. @moret
  readonly onCopyError: NonNullable<CopyContentTriggerUtilConfig['onCopyError']>
  // TODO: refactor ported specs allowing property to become private/protected and readonly. @moret
  readonly onCopySuccess: NonNullable<CopyContentTriggerUtilConfig['onCopySuccess']>
  private readonly showBoxLockModal?: CopyContentTriggerUtilConfig['showBoxLockModal']
  private readonly onVisibleChange?: CopyContentTriggerUtilConfig['onVisibleChange']
  // TODO: refactor ported specs allowing property to become private/protected and readonly. @moret
  viewer?: CopyContentTriggerUtilViewer

  constructor({
    window: windowProp,
    boxModalOptions,
    boxModalVersion,
    boxOptions,
    element,
    eventData,
    eventNamespace,
    product,
    featureCode,
    loadBoxModalData = true,
    onCopyError = noop,
    onCopySuccess = noop,
    showBoxLockModal,
    sendGA4Event,
    ga4EventData,
    onVisibleChange,
    onLoadViewerCanUseFeature,
  }: CopyContentTriggerUtilConfig) {
    this.window = windowProp ?? window
    this.boxModalOptions = boxModalOptions
    this.boxModalVersion = boxModalVersion
    this.boxOptions = boxOptions
    this.document = {}
    this.element = element
    this.eventData = {
      ...eventData,
      from_component: (eventData && eventData.from_component) || 'CopyContentTrigger',
    }
    this.eventNamespace = eventNamespace
    this.product = product
    this.sendGA4Event = sendGA4Event
    this.featureCode = featureCode
    this.loadBoxModalData = loadBoxModalData
    this.loading = true
    this.onCopyError = onCopyError
    this.onCopySuccess = onCopySuccess
    this.showBoxLockModal = showBoxLockModal
    this.onVisibleChange = onVisibleChange
    this.ga4EventData = ga4EventData

    this.extractDocFieldsFromElement()

    this.assignDocAndViewerFields()
      .then(() => {
        this.loading = false

        onLoadViewerCanUseFeature?.(!!this.document.viewerCanUseFeature)

        return this.viewer ? this.loadExternalDependencies() : Promise.resolve()
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err)
        this.loading = false
      })
      .then(() => {
        if (this.viewer && !this.document.viewerCanUseFeature) {
          this.initBoxModal()
        }
      })

    this.bindWindowMethods()
  }

  // TODO: refactor ported specs allowing private methods. @moret
  extractDocFieldsFromElement(): void {
    const artifact = this.element.getAttribute('data-doc-artifact')
    const docId = parseInt(this.element.getAttribute('data-doc-id') || '', 10)
    if (!artifact || !docId) {
      throw new Error('Invalid element, must have both artifact and docId values in attributes')
    }
    this.document = { ...this.document, artifact, docId }
  }

  // TODO: refactor ported specs allowing private methods. @moret
  assignDocAndViewerFields(): Promise<void> {
    const variables = {
      artifact: this.document.artifact,
      docId: this.document.docId,
      featureCode: this.featureCode,
      appId: this.eventData.app_id,
      fromComponent: this.eventData.from_component,
      boxOptions: this.boxOptions,
    }

    return CopyContentTriggerUtil.queryDocAndViewer(variables, this.loadBoxModalData).then(
      ({ document, viewer }) => {
        this.document = { ...this.document, ...document }
        this.viewer = { ...this.viewer, ...viewer }
      }
    )
  }

  bindWindowMethods(): void {
    if (!this.window) return
    const prevCallback = this.window.onCloseBoxLockModal
    this.window.onCloseBoxLockModal = () => {
      prevCallback?.()
      this.onVisibleChange?.(false)
    }
  }

  // TODO: refactor ported specs allowing private methods. @moret
  async loadExternalDependencies(): Promise<void> {
    const { boxModalVersion, document, loadBoxModalData } = this

    if (!document.viewerCanUseFeature && loadBoxModalData) {
      return loadBoxModal(boxModalVersion, this.window)
    }
    return Promise.resolve()
  }

  public addCopyListener(): void {
    if (!this.listenerHelper) {
      this.listenerHelper = new CopyListenerHelper(this.onCopyContent, this.element)
    }

    this.listenerHelper.enable()
  }

  public removeCopyListener(): void {
    if (this.listenerHelper) {
      this.listenerHelper.disable()
    }
  }

  // TODO: refactor ported specs allowing private methods. @moret
  readonly onCopyContent = (event: Event): void => {
    const { viewer, document } = this

    if (!isElementSelected(this.element)) {
      return
    }

    if (this.loading) {
      event.preventDefault()
      return
    }

    if (!viewer) {
      // This case is handled by CopyRequireLoginTrigger
      return
    }

    if (!document.viewerCanUseFeature) {
      this.handleCopyContentLock(event)
      this.showBoxModal()
      this.registerGAFeatureUseEvent(false)
    } else {
      this.onVisibleChange?.(false)
      this.useFeature()
      this.sendMetrics('CopiedContent', {
        browser_hash: this.getBrowserHashCookie(),
        metadata: {
          context_copy: this.listenerHelper?.getContextCopy() || null,
          selected_length: this.getSelectedLength() || null,
          first_selected_chars: this.getSelectedContentShort() || null,
          last_selected_chars: this.getLastSelectedContentShort() || null,
        },
      })

      this.registerGAFeatureUseEvent(true)
      this.onCopySuccess(event)
    }
  }

  private handleCopyContentLock(event: Event): void {
    this.sendMetrics('SawCopyContentLock')
    event.preventDefault()
    window.getSelection()?.removeAllRanges()
    this.onCopyError(event)
  }

  private buildBoxModalData() {
    const { boxModalOptions, boxOptions, eventData, featureCode, viewer } = this

    const { from_component: fromComponent, app_id: appId } = eventData

    return {
      appId,
      boxOptions,
      featureCode,
      fromComponent,
      ...boxModalOptions,
      ...(viewer?.modalData || {}),
      modalType: CopyContentTriggerUtil.boxModalType,
    }
  }

  // TODO: refactor ported specs allowing private methods. @moret
  initBoxModal(): void {
    const { initBoxLockModal = noop } = this.window ?? {}
    initBoxLockModal(this.buildBoxModalData())
  }

  // TODO: refactor ported specs allowing private methods. @moret
  showBoxModal(): void {
    const { showBoxLockModal = this.window?.showBoxLockModal } = this
    if (!showBoxLockModal) {
      throw new Error('BoxModal is not loaded')
    }

    showBoxLockModal(this.buildBoxModalData())
    this.onVisibleChange?.(true)
  }

  private getBrowserHashCookie(): string {
    const cookie = getCookie(null, cookieKey)

    if (!cookie) {
      const randomUniqueHash = uuid()
      const expires = new Date()
      expires.setFullYear(expires.getFullYear() + BROWSER_HASH_COOKIE_DURATION_IN_YEARS)
      setJusbrasilCookie(null, cookieKey, randomUniqueHash, { expires })
      return randomUniqueHash
    }

    return cookie
  }

  private getSelectedContentShort(): string | undefined {
    return window.getSelection()?.toString().substr(0, 20)
  }

  private getLastSelectedContentShort(): string | undefined {
    return window.getSelection()?.toString().substr(-20)
  }

  private getSelectedLength(): number | undefined {
    return window.getSelection()?.toString().length
  }

  // TODO: refactor ported specs allowing private methods. @moret
  readonly useFeature = once((): void => {
    const variables = {
      input: {
        artifact: this.document.artifact,
        docId: this.document.docId,
        feature: this.featureCode,
        fromComponent: this.eventData.from_component,
      },
    }
    client.mutate<CopyContentTriggerMutationMutation>({
      mutation: copyContentTriggerMutation,
      variables,
    })
  })

  private registerGAFeatureUseEvent(successfulAttempt: boolean): void {
    const {
      document: { docId, artifact },
    } = this

    if (this.sendGA4Event && this.ga4EventData) {
      const { componentClass, componentName, copyFeatureName } = this.ga4EventData

      this.sendGA4Event(
        new GA4FeatureUseEvent({
          componentClass,
          componentName,
          featureClass: FEATURE_CLASS.COPY_CONTENT,
          featureName: copyFeatureName,
          resourceId: `${artifact}:${docId}`,
          successfulAttempt,
          requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
        })
      )
    }
  }

  // TODO: refactor ported specs allowing private methods. @moret
  sendMetrics(
    eventName: string,
    extraProps: { browser_hash?: string; metadata?: JsonObject } = {}
  ): void {
    const {
      document: { docId, artifact },
      eventData,
      eventNamespace,
      viewer,
    } = this
    const metadata = { ...eventData.metadata, ...extraProps.metadata }

    sendMetrics(`${eventNamespace}.${eventName}`, `${viewer?.pid}`, {
      ...eventData,
      artifact,
      doc_id: docId,
      ...extraProps,
      metadata,
    })
  }
}
